"use client";

import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { TrendingUp, TrendingDown, Minus, Activity, Sparkles } from "lucide-react";
import AiInvestmentForm from "../ai-investment";
import { cn } from "@/lib/utils";
import { $fetch } from "@/lib/api";

interface StocksTradingFormProps {
  symbol: string;
  onOrderSubmit?: (order: any) => void;
}

interface StockData {
  symbol: string;
  price: number;
  change: number;
  changePercent: number;
  volume: number;
  marketCap: number;
  currency: string;
  exchangeName: string;
  timestamp: string;
  open?: number;
  high?: number;
  low?: number;
  close?: number;
  previousClose?: number;
}

export default function StocksTradingForm({
  symbol,
  onOrderSubmit,
}: StocksTradingFormProps) {
  const [stockData, setStockData] = useState<StockData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAiInvestment, setShowAiInvestment] = useState(false);

  useEffect(() => {
    const fetchStockData = async () => {
      if (!symbol) return;
      try {
        setIsLoading(true);
        setError(null);
        const response = await $fetch({
          url: `/api/yahoo-finance/ticker/${symbol}`,
          silent: true,
        });
        if (response.data) {
          const data = response.data;
          setStockData({
            symbol: data.symbol,
            price: data.price,
            change: data.change,
            changePercent: data.changePercent,
            volume: data.volume,
            marketCap: data.marketCap,
            currency: data.currency,
            exchangeName: data.exchangeName,
            timestamp: data.timestamp,
            open: data.open,
            high: data.high,
            low: data.low,
            close: data.close,
            previousClose: data.previousClose,
          });
        } else {
          throw new Error('No data received from Yahoo Finance API');
        }
      } catch (err) {
        console.error('Error fetching stock data:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch stock data');
        setStockData(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchStockData();
  }, [symbol]);

  const trend = useMemo(() => {
    if (!stockData) return "hold";
    if (stockData.changePercent > 0.5) return "buy";
    if (stockData.changePercent < -0.5) return "sell";
    return "hold";
  }, [stockData]);

  const formatCurrency = (value: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    if (value >= 1e9) {
      return (value / 1e9).toFixed(2) + 'B';
    } else if (value >= 1e6) {
      return (value / 1e6).toFixed(2) + 'M';
    } else if (value >= 1e3) {
      return (value / 1e3).toFixed(2) + 'K';
    }
    return value.toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading stock data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-2">Error loading stock data</div>
          <div className="text-sm text-muted-foreground">{error}</div>
        </div>
      </div>
    );
  }

  if (!stockData) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-muted-foreground">No stock data available</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col space-y-4 p-4">
      {/* Stock Price Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold">
              {stockData.symbol}
            </CardTitle>
            <Badge variant="secondary" className="text-xs">
              {stockData.exchangeName}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold">
                {formatCurrency(stockData.price, stockData.currency)}
              </div>
              <div className={cn(
                "text-sm font-medium flex items-center gap-1",
                stockData.change >= 0 ? "text-green-600" : "text-red-600"
              )}>
                {stockData.change >= 0 ? (
                  <TrendingUp className="h-3 w-3" />
                ) : (
                  <TrendingDown className="h-3 w-3" />
                )}
                {stockData.change >= 0 ? '+' : ''}
                {formatCurrency(stockData.change, stockData.currency)}
                ({stockData.changePercent >= 0 ? '+' : ''}{stockData.changePercent.toFixed(2)}%)
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-muted-foreground">Volume</div>
              <div className="font-medium">{formatNumber(stockData.volume)}</div>
            </div>
          </div>

          {/* Market Data */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-muted-foreground">Market Cap</div>
              <div className="font-medium">{formatNumber(stockData.marketCap)}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Currency</div>
              <div className="font-medium">{stockData.currency}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Trading Actions */}
      <div className="flex gap-2">
        <Button
          className="flex-1 bg-green-600 hover:bg-green-700"
          size="sm"
        >
          Buy
        </Button>
        <Button
          className="flex-1 bg-red-600 hover:bg-red-700"
          size="sm"
        >
          Sell
        </Button>
      </div>

      {/* Market Analysis */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Market Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Trend:</span>
              <Badge
                variant={trend === "buy" ? "default" : trend === "sell" ? "destructive" : "secondary"}
                className="text-xs"
              >
                {trend.toUpperCase()}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span>Signal:</span>
              <span className={cn(
                "font-medium",
                trend === "buy" ? "text-green-600" :
                trend === "sell" ? "text-red-600" : "text-muted-foreground"
              )}>
                {trend === "buy" ? "Bullish" :
                 trend === "sell" ? "Bearish" : "Neutral"}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AI Investment */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            AI Investment Assistant
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Button
            onClick={() => setShowAiInvestment(!showAiInvestment)}
            variant="outline"
            className="w-full"
            size="sm"
          >
            {showAiInvestment ? "Hide" : "Show"} AI Recommendations
          </Button>
        </CardContent>
      </Card>

      {showAiInvestment && (
        <AiInvestmentForm symbol={symbol} />
      )}
    </div>
  );
}

